import React, { useState, useEffect } from 'react';
import { fetchWithAuth } from '../utils/auth';
import { API_URL } from '../config';

// Add new pages here as they're introduced — no backend changes needed.
const PERMISSION_LABELS = {
  calendar:  'Calendar',
  questions: 'Questions',
  games:     'Games',
  faq:       'FAQ Management',
  users:     'User Management',
  branches:  'Branch Management',
  student_registration: 'Student Registration',
  grade_evaluation: 'Grade Evaluation',
};

const permissions = Object.keys(PERMISSION_LABELS);

const AdminRolePermissions = () => {
  const [roles, setRoles]         = useState([]);   // [{role, label, is_builtin, permissions}]
  const [draft, setDraft]         = useState([]);   // editable copy
  const [loading, setLoading]     = useState(true);
  const [saving, setSaving]       = useState(false);
  const [error, setError]         = useState('');
  const [saved, setSaved]         = useState(false);
  const [showAddForm, setShowAddForm] = useState(false);
  const [newRole, setNewRole]     = useState({ label: '', permissions: {} });
  const [adding, setAdding]       = useState(false);

  useEffect(() => { loadRoles(); }, []);

  const loadRoles = async () => {
    setLoading(true);
    setError('');
    try {
      const res = await fetchWithAuth(`${API_URL}/api/admin/role-permissions`);
      const data = await res.json();
      if (res.ok) {
        setRoles(data.roles);
        setDraft(JSON.parse(JSON.stringify(data.roles)));
      } else {
        setError(data.error || 'Failed to load');
      }
    } catch {
      setError('Network error');
    } finally {
      setLoading(false);
    }
  };

  const togglePerm = (roleKey, perm) => {
    if (roleKey === 'super_admin') return;
    setDraft(prev => prev.map(r =>
      r.role === roleKey
        ? { ...r, permissions: { ...r.permissions, [perm]: !r.permissions[perm] } }
        : r
    ));
    setSaved(false);
  };

  const handleSave = async () => {
    setSaving(true);
    setError('');
    setSaved(false);
    try {
      // Build updates map: {role: {permissions, label}}
      const updates = {};
      draft.forEach(r => { updates[r.role] = { permissions: r.permissions, label: r.label }; });
      const res = await fetchWithAuth(`${API_URL}/api/admin/role-permissions`, {
        method: 'PUT',
        body: JSON.stringify({ role_permissions: updates }),
      });
      const data = await res.json();
      if (res.ok && data.success) {
        setRoles(data.roles);
        setDraft(JSON.parse(JSON.stringify(data.roles)));
        setSaved(true);
      } else {
        setError(data.error || 'Save failed');
      }
    } catch {
      setError('Network error');
    } finally {
      setSaving(false);
    }
  };

  const handleAddRole = async () => {
    if (!newRole.label.trim()) { setError('Role name is required'); return; }
    setAdding(true);
    setError('');
    try {
      const res = await fetchWithAuth(`${API_URL}/api/admin/roles`, {
        method: 'POST',
        body: JSON.stringify({ label: newRole.label.trim(), permissions: newRole.permissions }),
      });
      const data = await res.json();
      if (res.ok && data.success) {
        setRoles(data.roles);
        setDraft(JSON.parse(JSON.stringify(data.roles)));
        setShowAddForm(false);
        setNewRole({ label: '', permissions: {} });
      } else {
        setError(data.error || 'Failed to create role');
      }
    } catch {
      setError('Network error');
    } finally {
      setAdding(false);
    }
  };

  const handleDeleteRole = async (roleKey, label) => {
    if (!window.confirm(`Delete role "${label}"? Users with this role will need to be reassigned.`)) return;
    setError('');
    try {
      const res = await fetchWithAuth(`${API_URL}/api/admin/roles/${roleKey}`, { method: 'DELETE' });
      const data = await res.json();
      if (res.ok && data.success) {
        setRoles(data.roles);
        setDraft(JSON.parse(JSON.stringify(data.roles)));
      } else {
        setError(data.error || 'Delete failed');
      }
    } catch {
      setError('Network error');
    }
  };

  const isDirty = JSON.stringify(draft) !== JSON.stringify(roles);

  if (loading) return <div style={{ padding: '24px' }}>Loading role permissions...</div>;

  return (
    <div style={{ padding: '24px', maxWidth: '960px' }}>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '6px' }}>
        <h2 style={{ margin: 0 }}>Role Permissions</h2>
        <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
          {saved && <span style={{ color: '#22c55e', fontSize: '14px' }}>Saved</span>}
          {isDirty && (
            <button className="btn-secondary btn-sm" onClick={() => { setDraft(JSON.parse(JSON.stringify(roles))); setSaved(false); }}>
              Reset
            </button>
          )}
          <button className="btn-primary btn-sm" onClick={handleSave} disabled={saving || !isDirty}>
            {saving ? 'Saving…' : 'Save Changes'}
          </button>
          <button className="btn-primary btn-sm" onClick={() => { setShowAddForm(true); setError(''); }}>
            + Add Role
          </button>
        </div>
      </div>

      <p style={{ color: '#666', fontSize: '13px', marginBottom: '16px' }}>
        Toggle page access per role. Changes take effect on the user's next login.
        Super Admin always has full access. Built-in roles cannot be deleted.
      </p>

      {error && <div className="error-banner" style={{ marginBottom: '12px' }}>{error}</div>}

      {/* Add Role Form */}
      {showAddForm && (
        <div className="form-card" style={{ marginBottom: '20px' }}>
          <h4 style={{ margin: '0 0 12px' }}>New Role</h4>
          <div className="form-row">
            <label>Role Name *</label>
            <input
              type="text"
              value={newRole.label}
              onChange={e => setNewRole({ ...newRole, label: e.target.value })}
              placeholder="e.g. Assistant Teacher"
              autoFocus
            />
            <small style={{ color: '#888', marginTop: '4px', display: 'block' }}>
              Role key will be auto-generated: <code>{newRole.label.toLowerCase().replace(/\s+/g, '_').replace(/[^a-z0-9_]/g, '') || '…'}</code>
            </small>
          </div>
          <div style={{ marginBottom: '12px' }}>
            <label style={{ display: 'block', fontWeight: '600', fontSize: '13px', marginBottom: '8px' }}>Initial Permissions</label>
            <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap' }}>
              {permissions.map(perm => (
                <label key={perm} style={{ display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer', fontSize: '14px' }}>
                  <input
                    type="checkbox"
                    checked={!!newRole.permissions[perm]}
                    onChange={e => setNewRole({ ...newRole, permissions: { ...newRole.permissions, [perm]: e.target.checked } })}
                  />
                  {PERMISSION_LABELS[perm]}
                </label>
              ))}
            </div>
          </div>
          <div className="form-actions">
            <button className="btn-primary btn-sm" onClick={handleAddRole} disabled={adding}>
              {adding ? 'Creating…' : 'Create Role'}
            </button>
            <button className="btn-secondary btn-sm" onClick={() => { setShowAddForm(false); setNewRole({ label: '', permissions: {} }); }}>
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* Permissions Matrix */}
      <div style={{ overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '14px' }}>
          <thead>
            <tr style={{ background: '#f8fafc' }}>
              <th style={th({ textAlign: 'left', minWidth: '180px' })}>Role</th>
              {permissions.map(perm => (
                <th key={perm} style={th({ textAlign: 'center', minWidth: '110px' })}>
                  {PERMISSION_LABELS[perm]}
                </th>
              ))}
              <th style={th({ width: '60px' })}></th>
            </tr>
          </thead>
          <tbody>
            {draft.map((r, i) => {
              const locked = r.role === 'super_admin';
              return (
                <tr key={r.role} style={{ background: i % 2 === 0 ? '#fff' : '#f8fafc' }}>
                  <td style={td()}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                      <span className={`role-badge role-${r.role}`}>{r.label}</span>
                      {!r.is_builtin && (
                        <span style={{ fontSize: '10px', color: '#888', background: '#f3f4f6', padding: '1px 5px', borderRadius: '8px' }}>
                          custom
                        </span>
                      )}
                    </div>
                    <div style={{ fontSize: '11px', color: '#aaa', marginTop: '2px' }}>{r.role}</div>
                  </td>
                  {permissions.map(perm => {
                    const enabled = r.permissions[perm] ?? false;
                    return (
                      <td key={perm} style={td({ textAlign: 'center' })}>
                        <button
                          onClick={() => togglePerm(r.role, perm)}
                          disabled={locked}
                          title={locked ? 'Super Admin cannot be restricted' : (enabled ? 'Click to disable' : 'Click to enable')}
                          style={{
                            width: '34px', height: '34px', borderRadius: '50%',
                            border: '2px solid',
                            borderColor: enabled ? '#22c55e' : '#d1d5db',
                            background: enabled ? '#dcfce7' : '#f3f4f6',
                            color: enabled ? '#16a34a' : '#9ca3af',
                            cursor: locked ? 'not-allowed' : 'pointer',
                            fontSize: '16px',
                            display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                            transition: 'all 0.15s',
                            opacity: locked ? 0.6 : 1,
                          }}
                        >
                          {enabled ? '✓' : '✕'}
                        </button>
                      </td>
                    );
                  })}
                  <td style={td({ textAlign: 'center' })}>
                    {!r.is_builtin && (
                      <button
                        className="btn-sm btn-danger"
                        onClick={() => handleDeleteRole(r.role, r.label)}
                        title="Delete custom role"
                      >
                        Delete
                      </button>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <div style={{ marginTop: '20px', padding: '10px 14px', background: '#f8fafc', borderRadius: '8px', fontSize: '12px', color: '#666' }}>
        To add a new <strong>page</strong> (e.g. FAQ), add its key to <code>PERMISSION_LABELS</code> in <code>AdminRolePermissions.js</code> and add the corresponding nav check. The DB stores and serves it automatically.
      </div>
    </div>
  );
};

const th = (extra = {}) => ({
  padding: '10px 14px', fontWeight: '600',
  borderBottom: '2px solid #e5e7eb', ...extra,
});
const td = (extra = {}) => ({
  padding: '10px 14px', borderBottom: '1px solid #e5e7eb',
  verticalAlign: 'middle', ...extra,
});

export default AdminRolePermissions;
